import { useCallback, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { emit } from '../../platform/analytics/analytics'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { Button } from '../../platform/primitives/Button'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { useToast } from '../../platform/feedback/ToastProvider'
import { accountBalancesStaleLabel, connectionsStaleLabel } from './accountsStaleLabels'
import { ConnectionCard } from './ConnectionCard'
import { connectionsListState, healthFor, isSyncable, linkedGroupsOf, relinkIdOf, type InstitutionGroup } from './connectionGroups'
import { ReconnectAction } from './ReconnectAction'
import { activeRelinkFor, MatchAccountsLink, RelinkNotices } from './RelinkNotices'
import { useReauthResultToasts } from './useRelinkOutcome'
import { aggregatorProviderCode } from './providers/registry'
import { useAccounts } from './useAccounts'
import { useProviderItems, useReauthFlow, type ProviderItem } from './useAggregator'
import { useDeleteItem } from './useDeleteItem'
import { summarizeSync, useSyncItem } from './useSyncItem'
import './accounts.css'

const SKELETON = (
  <>
    <Skeleton height={60} />
    <Skeleton height={60} />
  </>
)

/**
 * What `ConfirmDialog` shows before the DELETE — M5-R19/spec §C. Names the
 * real account count so nobody reads a rounded "your accounts" as a hedge.
 *
 * **Deliberately does NOT say reconnecting later means linking from
 * scratch.** That claim was never traced against the backend (spec §C) and
 * is absent on purpose, not by oversight — `Connections.test.tsx` asserts
 * its absence.
 */
function disconnectBody(group: InstitutionGroup): string {
  const n = group.accounts.length
  return (
    'TrackE5 will stop receiving updates and will revoke its access at the bank. ' +
    `Your ${n} account${n === 1 ? '' : 's'} and their transaction history stay — ` +
    "they just won't update again."
  )
}

/**
 * `/connections` — Ticket 3 (#425), rebuilt as always-open cards (Task 5,
 * closes #442).
 *
 * **One `useReauthFlow()`/`useSyncItem()`/`useDeleteItem()` for the whole
 * screen**, not one per card — only one reconnect can be in flight at a time
 * (the popup/SDK it opens is modal by construction), and the two mutations
 * carry no per-card state of their own beyond `reauthingId`/
 * `pendingDisconnect`.
 *
 * **Two nested `<Async>`s, not one.** `surface="aggregator-items"` is the
 * page level (this screen's own subject); `surface="accounts"` is nested
 * around every card body, because with an always-open card the account list
 * IS the card body — a failed accounts fetch must not render as a connection
 * with zero accounts (#442's own trap, and this screen's Async warning).
 * ONE nested `<Async>`, not one per card: a single `useAccounts()` query
 * feeds every card, and N nested `<Async>`s would fire N skeletons for one
 * request. Direction is the inverse of `AccountsSummary.tsx`'s Connections
 * tile, which nests `aggregator-items` inside `accounts`.
 */
export function Connections() {
  const navigate = useNavigate()
  const toast = useToast()
  const rawItemsState = useProviderItems()
  const accountsState = useAccounts()
  // Emptiness is judged on the joined GROUPS, never on raw items — see
  // `connectionsListState` for how the two queries combine.
  const itemsState = connectionsListState(rawItemsState, accountsState)
  const reauth = useReauthFlow()
  const sync = useSyncItem()
  const del = useDeleteItem()
  const [reauthingId, setReauthingId] = useState<string | null>(null)
  // Per-card, like `reauthingId` — I3, fix round 1 review. `sync.isPending`
  // is the ONE mutation's in-flight flag, shared across every card on the
  // page; gating a card's own disabled/label on it directly meant pressing
  // Sync now on one connection relabelled and disabled every OTHER card's
  // button too, reporting a sync of a connection nobody asked to sync.
  //
  // **A `Set`, not a single id — NEW-1, fix round 2 re-review.** A single
  // `string | null` slot fixes the page-wide case but still mis-reports two
  // OVERLAPPING syncs: with two connections syncing at once, the first to
  // resolve's unconditional `.finally(() => setSyncingId(null))` clears the
  // SECOND card's still-running "Syncing…" too — the identical class of lie
  // I3 fixed, one click narrower. A `Set` lets each press add its own id and
  // each `.finally` remove only that id, so a still-open sync on one card
  // survives another card's sync finishing.
  const [syncingIds, setSyncingIds] = useState<Set<string>>(() => new Set())
  const [pendingDisconnect, setPendingDisconnect] = useState<InstitutionGroup | null>(null)

  const settleReauth = useCallback(() => setReauthingId(null), [])
  useReauthResultToasts(reauth, settleReauth)

  /** A disconnected bank whose reconnect is pending or needs review. */
  function isMatching(group: InstitutionGroup, items: ProviderItem[]): boolean {
    if (healthFor(group)?.kind !== 'disconnected') return false
    const oldId = group.accounts[0]?.itemId ?? null
    return oldId !== null && activeRelinkFor(items, oldId) !== null
  }

  /** The actions slot for one card. `null` for a group with no resolvable
   *  item (`item === null`) or a disconnected one — there is nothing to sync,
   *  repair or disconnect. */
  function actionsFor(group: InstitutionGroup, items: ProviderItem[]): ReactNode {
    const item = group.item
    const health = healthFor(group)
    // Disconnected: no Sync now, no repair, no Disconnect — even when the items
    // endpoint still lists the item (a provider-removed one). A reconnectable one
    // gets `ReconnectAction`, which is a NEW connection taking over the old
    // accounts, not a repair of this one; a bank with nothing to reconnect to
    // gets nothing. Checked BEFORE `item === null`: a user-disconnected item is
    // absent from the items list, and is still reconnectable.
    if (health?.kind === 'disconnected') {
      // A reconnect already under way for this bank: finish THAT, rather than
      // offering a second Reconnect (the old group is no longer reconnectable).
      const oldId = group.accounts[0]?.itemId ?? null
      const active = oldId === null ? null : activeRelinkFor(items, oldId)
      if (active !== null) return <MatchAccountsLink newItemId={active.id} />
      return relinkIdOf(group) === null ? null : <ReconnectAction group={group} entryPoint="connections" />
    }
    if (!isSyncable(group) || item === null) return null

    return (
      <>
        {health?.kind === 'reauth' ? (
          <Button
            variant="chip"
            disabled={reauthingId === item.id}
            aria-busy={reauthingId === item.id ? true : undefined}
            className={reauthingId === item.id ? 'reconnect-busy' : undefined}
            onClick={() => {
              setReauthingId(item.id)
              void reauth.start(item.id, item.provider)
            }}
          >
            {reauthingId === item.id ? 'Reconnecting…' : 'Reconnect'}
          </Button>
        ) : (
          <Button
            variant="chip"
            disabled={syncingIds.has(item.id)}
            onClick={() => {
              // Once per press, matching `AccountDetail.tsx`'s own single-item
              // case (B1, architect review, #399) — this is not a fan-out, so
              // there is no width to record.
              emit('sync_triggered', { scope: 'item' })
              setSyncingIds((prev) => new Set(prev).add(item.id))
              sync
                .sync(item.id)
                .then((result) => {
                  toast.show({ kind: 'success', message: summarizeSync(result) })
                })
                .catch(() => toast.show({ kind: 'error', message: 'Sync could not be started.' }))
                .finally(() =>
                  setSyncingIds((prev) => {
                    // Remove only THIS id — a still-open sync on another card
                    // must survive this one settling (NEW-1).
                    const next = new Set(prev)
                    next.delete(item.id)
                    return next
                  }),
                )
            }}
          >
            {syncingIds.has(item.id) ? 'Syncing…' : 'Sync now'}
          </Button>
        )}
        <span className="connections__spacer" />
        <Button variant="destructive" onClick={() => setPendingDisconnect(group)}>
          Disconnect
        </Button>
      </>
    )
  }

  return (
    <>
      <header className="connections__header">
        <div className="connections__titles">
          <h2 className="connections__title">Connections</h2>
          <p className="connections__lede">
            Your accounts keep their history even if you disconnect.
          </p>
        </div>
        {/* Rule 9: this is the ONE --accent fill on the page. Reconnect above
            is a chip (decision 6, recorded in the spec as deliberate). */}
        <Button variant="primary" onClick={() => navigate('/accounts/connect')}>
          ＋ Link a bank
        </Button>
      </header>

      <Async
        surface="aggregator-items"
        state={itemsState}
        skeleton={SKELETON}
        staleLabel={connectionsStaleLabel}
        empty={{
          title: 'No connected banks yet.',
          action: { label: 'Link a bank', onAct: () => navigate('/accounts/connect') },
        }}
      >
        {(items) => (
          <Async
            surface="accounts"
            // `useAccounts()`'s own `isEmpty` collapses a household with
            // literally zero accounts into `status: 'empty'` — the SAME
            // "data-shaped emptiness" every other `accounts` caller sees. On
            // THIS screen that is not a reason to hide every card: a freshly
            // linked connection whose first sync has not landed (#442) is
            // exactly zero accounts everywhere, and the fix this screen ships
            // is that its card still renders, with a working "Sync now".
            // Folded into `success` with `data: []` for THIS nested surface
            // only — never for `error` (still renders the real failure below,
            // #442's other half) and never for the OUTER `aggregator-items`
            // surface, which keeps its own genuine "No connected banks yet."
            // empty state untouched.
            state={accountsState.status === 'empty' ? { status: 'success', data: [] } : accountsState}
            skeleton={SKELETON}
            staleLabel={accountBalancesStaleLabel}
            // Unreachable given the remap above — kept because `<Async>`'s
            // `empty` prop is required regardless of what a given caller's
            // state can structurally reach (`Props<T>.empty` has no
            // conditional variant), the same "kept so a future caller cannot
            // forget it" reasoning `AccountDetail.tsx`'s own unreachable
            // `empty` prop states.
            empty={{ title: 'No accounts yet.' }}
          >
            {(accounts) => (
              <>
              <RelinkNotices items={items} />
              <div role="list" aria-label="Connections">
                {linkedGroupsOf(accounts, items).map((group) => (
                  <ConnectionCard
                    key={group.key}
                    group={group}
                    health={healthFor(group)}
                    matching={isMatching(group, items)}
                    actions={actionsFor(group, items)}
                  />
                ))}
              </div>
              </>
            )}
          </Async>
        )}
      </Async>

      <ConfirmDialog
        open={pendingDisconnect !== null}
        mode="disconnect_connection"
        destructive
        title={pendingDisconnect ? `Disconnect ${pendingDisconnect.name}?` : ''}
        body={pendingDisconnect ? disconnectBody(pendingDisconnect) : ''}
        confirmLabel="Disconnect"
        onCancel={() => setPendingDisconnect(null)}
        onConfirm={async () => {
          if (pendingDisconnect === null || pendingDisconnect.item === null) return
          const { item, name } = pendingDisconnect
          await del.remove(item.id)
          // After the DELETE resolves, so a cancel or a rejection (which
          // throws above) never counts. `provider` goes through the closed
          // `aggregatorProviderCode` set, never the wire string.
          emit('aggregator_item_disconnected', {
            provider: aggregatorProviderCode(item.provider),
            account_count: pendingDisconnect.accounts.length,
          })
          toast.show({ kind: 'success', message: `${name} disconnected.` })
          setPendingDisconnect(null)
        }}
      />
    </>
  )
}
